
import * as constants from "src/utils/constants";
import { useEffect, useState, useCallback } from 'react';
import { Outlet, useLocation, useNavigate } from 'react-router-dom';
import { styled } from '@mui/material/styles';

import DashboardSidebar from './DashboardSidebar';
import LoggedOut from 'src/pages/LoggedOut';
import { Alert, Box, Button, Dialog, DialogContent, DialogTitle, IconButton, useMediaQuery } from '@mui/material';
import { Close, Warning as WarningIcon } from "@mui/icons-material";
import { useTheme } from '@emotion/react';
import UserInfo from 'src/utils/Authorization/UserInfo';
import axios from 'axios';
// import Changelog from './Changelog';

// ----------------------------------------------------------------------

const RootStyle = styled('div')({
  display: 'flex',
  height: '100%',
  overflow: 'hidden'
});

const MainStyle = styled('div')(({ theme, isPaddingTop=false }) => ({
  flexGrow: 1,
  overflow: 'auto',
  height: '100vh',
  // height: isJourneyPage ? '100vh' : 'auto',
  // minHeight: isJourneyPage ? 'unset' : '100%',
  paddingTop: isPaddingTop ? 0 : theme.spacing(3),
  // paddingBottom: isJourneyPage ? 0 : theme.spacing(10),
  // [theme.breakpoints.up('lg')]: {
  //   paddingTop: isJourneyPage ? 0 : theme.spacing(3),
  //   paddingLeft: theme.spacing(2),
  //   paddingRight: theme.spacing(2)
  // }
}));

// ----------------------------------------------------------------------

const API_URL = `${constants.HTTP_METHOD}${constants.HTTP_URL}${constants.HTTP_PORT}`;

export default function DashboardLayout() {
  const userInfo = UserInfo();
  const emotionTheme = useTheme();
  const matches = useMediaQuery(emotionTheme.breakpoints.down('md'));
  const location = useLocation();
  const navigate = useNavigate();

  // Outlook connection warning state
  const [outlookWarning, setOutlookWarning] = useState(null);
  const [outlookBannerDismissed, setOutlookBannerDismissed] = useState(false);

  const PAGES_WITHOUT_PADDING_TOP = ['/companies', '/dashboard', '/tasks', '/support'];
  const isPaddingTop = PAGES_WITHOUT_PADDING_TOP.find((page) => location.pathname.includes(page));

  // Check Outlook connection status
  const checkOutlookConnection = useCallback(async () => {
    const token = localStorage.getItem('authToken');
    if (!token) return;
    try {
      const response = await axios.get(
        `${API_URL}/api/v1/outlook/auth/status/`,
        { headers: { Authorization: `Token ${token}` } }
      );
      const data = response.data;
      if (data?.connected && data?.connections?.length > 0) {
        const hasExpired = data.connections.some(c => c.token_expired);
        if (hasExpired) {
          const expiredEmails = data.connections.filter(c => c.token_expired).map(c => c.email);
          setOutlookWarning({
            type: 'expired',
            message: `Your Outlook connection (${expiredEmails.join(', ')}) has expired. Please reconnect to continue syncing emails, calendar & meeting recordings.`
          });
        } else {
          setOutlookWarning(null);
        }
      } else if (data && !data.connected && data.has_inactive_connections) {
        setOutlookWarning({
          type: 'disconnected',
          message: 'Your Outlook connection has been disconnected. Please reconnect to sync emails, calendar & meeting recordings.'
        });
      } else {
        setOutlookWarning(null);
      }
    } catch (err) {
      // Don't show warning on network errors — silently fail
    }
  }, []);

  // Check Outlook connection on mount and every 30 minutes
  useEffect(() => {
    if (!localStorage.getItem('authToken')) return;
    checkOutlookConnection();
    const interval = setInterval(checkOutlookConnection, 30 * 60 * 1000);
    return () => clearInterval(interval);
  }, [checkOutlookConnection]);

  return (
    <div>
      {localStorage.getItem('authToken') ?
        <>
          <RootStyle>
            <DashboardSidebar />
            <MainStyle isPaddingTop={isPaddingTop}>
              {/* Outlook Connection Warning Banner */}
              {outlookWarning && !outlookBannerDismissed && (
                <Box sx={{ px: 2, pt: 1 }}>
                  <Alert
                    severity="error"
                    variant="filled"
                    icon={<WarningIcon fontSize="small" />}
                    action={
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Button
                          color="inherit"
                          size="small"
                          variant="outlined"
                          onClick={() => navigate('/outlook-email')}
                          sx={{ 
                            textTransform: 'none', 
                            fontWeight: 600, 
                            borderColor: 'rgba(255,255,255,0.5)',
                            '&:hover': { borderColor: '#fff', bgcolor: 'rgba(255,255,255,0.1)' }
                          }}
                        >
                          Reconnect Now
                        </Button>
                        <IconButton
                          color="inherit"
                          size="small"
                          onClick={() => setOutlookBannerDismissed(true)}
                        >
                          <Close fontSize="small" />
                        </IconButton>
                      </Box>
                    }
                    sx={{ 
                      borderRadius: 1,
                      '& .MuiAlert-message': { display: 'flex', alignItems: 'center', fontWeight: 500 }
                    }}
                  >
                    {outlookWarning.message}
                  </Alert>
                </Box>
              )}
              <Outlet />
            </MainStyle>
          </RootStyle>

          {/* {latestRelease ?
            <Dialog
              maxWidth='lg'
              open={isChangelogOpen}
              keepMounted
              onClose={() => {setIsChangelogOpen(false)}}
            >
              <DialogTitle justifyContent="space-between" alignItems="center" display="flex" sx={{ paddingBottom: 0 }}>
                <>What's New - Latest Updates & Features</>
                <IconButton onClick={() => {setIsChangelogOpen(false)}}><Close /></IconButton>
              </DialogTitle>
              <DialogContent sx={{ p: 0 }} style={{display: "flex", flexDirection: "column"}}>
                <Changelog latestRelease={latestRelease} matches={matches} />
                <Button variant="text" size='small' color="primary" style={{alignSelf: "center", fontSize: "12px"}} onClick={handleDontShowAgain} sx={{ marginInline: 2, mb: 2 }}>
                  Don't show this again
                </Button>
              </DialogContent>
            </Dialog>
          : null } */}
        </>

        : <LoggedOut />}
    </div>
  );
}

